import React from "react";
import { Typography, Box } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import { Form, Formik } from "formik";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

import {
  ChangeNetworkDrawer,
  PreflightModalTransfer,
  TransferActiveModal,
  AboutDrawer,
  NetworkUnsupportedModal,
} from "../../modules";
import {
  AddressInput,
  TokenSelectInput,
  TokenInput,
  Fees,
} from "../../components";
import { WalletType } from "../../contexts/NetworkManagerContext/NetworkManagerContext";
import { BridgeConfig } from "../../chainbridgeConfig";
import { PreflightDetails } from "./TransferPage";
import { Button, TextButton } from "../../components/Button";
import { Select } from "../../components";
import { maskClasses } from "../../utils/classes";
import { InjectedAccountType } from "src/contexts/Adaptors/interfaces";


type DestinationChainsType = {
  name: string;
  chainId?: number | string; // TODO: re-check type
  domainId?: number;
};

type TransferPageView = {
  isReady: boolean | undefined;
  classes?: any;
  setWalletType: (walletType: WalletType) => void;
  walletType: string;
  walletConnecting: boolean;
  setChangeNetworkOpen: React.Dispatch<React.SetStateAction<boolean>>;
  homeConfig: BridgeConfig | undefined;
  accounts: Array<any> | undefined;
  selectAccount: any;
  transferSchema: object;
  setPreflightDetails: React.Dispatch<React.SetStateAction<PreflightDetails>>;
  setPreflightModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  preflightModalOpen: boolean;
  tokens: any;
  destinationChains: DestinationChainsType[];
  setDestinationChain: (chainId: number | undefined | any) => void;
  destinationChainConfig: any;
  showImageUrl: (url: string | undefined) => string;
  preflightDetails: PreflightDetails;
  bridgeFee: any;
  txFee: any;
  address: string | undefined;
  aboutOpen: any;
  setAboutOpen: React.Dispatch<React.SetStateAction<boolean>>;
  changeNetworkOpen: any;
  deposit: any;
  transactionStatus: string | undefined;
  resetDeposit: () => void;
  handleClick: (blockExplorer: string, txHash: string) => void;
};

export default function TransferPageView({
  isReady,
  setWalletType,
  walletType,
  walletConnecting,
  setChangeNetworkOpen,
  homeConfig,
  accounts,
  selectAccount,
  transferSchema,
  setPreflightDetails,
  setPreflightModalOpen,
  preflightModalOpen,
  tokens,
  destinationChains,
  setDestinationChain,
  destinationChainConfig,
  preflightDetails,
  bridgeFee,
  txFee,
  address,
  aboutOpen,
  setAboutOpen,
  changeNetworkOpen,
  deposit,
  transactionStatus,
  resetDeposit,
  handleClick,
}: TransferPageView) {
  const theme = useTheme();
  const handleChange = (event: SelectChangeEvent) => {
    setDestinationChain(event.target.value as any); // number | undefined TODO: re-check type
  };

  const accountsOptions = accounts?.map((account: InjectedAccountType) => ({
    value: account.address,
    name: account.meta?.name ?? account.address,
  }));
  const accountAddressValue = accounts?.find((v) => v.address === address);
  return (
    <Box
      position="relative"
      display="flex"
      flex="1"
      maxHeight={preflightModalOpen ? "788px" : "none"}
      height={preflightModalOpen ? "100%" : "auto"}
      width="100%"
    >
      {true && (
        <Box
          borderRadius={theme.spacing(5)}
          display={preflightModalOpen ? "none" : "flex"}
          flexDirection="column"
          alignItems="center"
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: "30px",
          }}
        >
          <Box flex={1} width="100%">
            {!isReady ? (
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setWalletType("select");
                }}
              >
                Connect
              </Button>
            ) : walletConnecting ? (
              <Box
                sx={{
                  textAlign: "center",
                  marginBottom: theme.spacing(4),
                }}
              >
                <Typography component="p" variant="h5">
                  This app requires access to your wallet, <br />
                  please login and authorize access to continue.
                </Typography>
              </Box>
            ) : (
              !preflightModalOpen && (
                <Box
                  sx={
                    preflightModalOpen || !!transactionStatus ? maskClasses : {}
                  }
                >
                  <Box
                    component="section"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={theme.spacing(5)}
                    sx={{
                      [theme.breakpoints.down("md")]: {
                        mb: 0,
                      },
                    }}
                  >
                    <Typography variant="body1" color="#fff" fontWeight="700">
                      Home network
                    </Typography>
                    <TextButton
                      variant="text"
                      onClick={() => setChangeNetworkOpen(true)}
                      sx={{
                        color: theme.palette.secondary.main,
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "1rem",
                      }}
                    >
                      Change
                    </TextButton>
                  </Box>
                  <Box
                    height="68px"
                    padding={theme.spacing(5)}
                    borderRadius={theme.spacing(4)}
                    border={`3px solid ${theme.palette.secondary.light}`}
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    fontWeight="700"
                    color={theme.palette.secondary.main}
                  >
                    {" "}
                    {homeConfig?.name}
                  </Box>
                </Box>
              )
            )}
          </Box>

          {isReady &&
            walletType === "Substrate" &&
            accounts &&
            accounts.length > 0 && (
              <Box mt="30px" width="100%" maxWidth="509px">
                <Select
                  label="Select account"
                  onChange={(value) => selectAccount(value)}
                  value={accountAddressValue?.address || ""}
                  placeholder="Select an account"
                  options={accountsOptions}
                />
              </Box>
            )}
          <Formik
            initialValues={{
              tokenAmount: 0,
              token: "",
              receiver: "",
              feeCheck: "",
            }}
            validateOnChange={false}
            validationSchema={transferSchema}
            onSubmit={(values) => {
              setPreflightDetails({
                ...values,
                tokenSymbol: tokens[values.token].symbol || "",
              });
              setPreflightModalOpen(true);
            }}
          >
            {(props) => {
              const choosenToken = props.values.token
                ? tokens[props.values.token]
                : "";

              const isNative = choosenToken && (choosenToken.symbol === homeConfig?.nativeTokenSymbol);
              const maxValue = choosenToken 
                ? isNative 
                  ? Math.max(choosenToken.balance - (bridgeFee ?? 0) - (txFee ?? 0), 0)
                  : choosenToken.balance // allow all
                : 0;

              return (
                <Box component={Form} width="100%" mt="30px">
                  <Box component="section">
                    <Select
                      onChange={(value) => handleChange(value)}
                      value={destinationChainConfig?.domainId || ""}
                      options={destinationChains.map(chain => ({...chain, value: chain.domainId}))}
                      disabled={!homeConfig}
                      label="Destination Network"
                    />
                  </Box>
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    component="section"
                    gap={theme.spacing(7)}
                    sx={{
                      margin: theme.spacing("30px", 0),
                      [theme.breakpoints.down("md")]: {
                        display: "flex",
                        flexDirection: "column",
                        gap: theme.spacing(5),
                      },
                    }}
                  >
                    <TokenSelectInput
                      tokens={tokens}
                      disabled={!destinationChainConfig}
                      label="Select token"
                      name="token"
                      // goes from formik --- START ---
                      value={props.values.token}
                      handleBlur={props.handleBlur}
                      setFieldValue={props.setFieldValue}
                      // --- END ---
                      sync={(tokenAddress) => {
                        // WHY DO WE NEED THIS
                        setPreflightDetails({
                          ...preflightDetails,
                          token: tokenAddress,
                          receiver: "",
                          tokenAmount: 0,
                          tokenSymbol: "",
                        });
                      }}
                      options={
                        Object.keys(tokens).map((t) => ({
                          value: t,
                          // icon: tokens[t]?.imageUri
                          //   ? showImageUrl(tokens[t]?.imageUri)
                          //   : null,
                          name: tokens[t]?.symbol || t,
                          symbol: tokens[t]?.symbol || t,
                        })) || []
                      }
                    />
                    <TokenInput
                      token={choosenToken}
                      disabled={
                        !destinationChainConfig ||
                        !preflightDetails.token ||
                        preflightDetails.token === ""
                      }
                      name="tokenAmount"
                      label="I want to send"
                      value={props.values?.tokenAmount}
                      maxValue={maxValue}
                      onChange={props.handleChange}
                      error={props.errors.tokenAmount}
                      setFieldValue={props.setFieldValue}
                    />
                  </Box>
                  {choosenToken ? (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      mb="30px"
                    >
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color={theme.palette.text.primary}
                      >
                        Balance
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color={theme.palette.text.primary}
                      >
                        {choosenToken.balance} {choosenToken.symbol}
                      </Typography>
                    </Box>
                  ) : (
                    ""
                  )}
                  <Box component="section" mb="30px">
                    <AddressInput
                      disabled={!destinationChainConfig}
                      name="receiver"
                      label="Destination Address"
                      placeholder="Please enter the receiving address"
                      senderAddress={`${address}`}
                      sendToSameAccountHelper={
                        destinationChainConfig?.type === homeConfig?.type
                      }
                      error={props.errors.receiver}
                      onChange={props.handleChange}
                      setFieldValue={props.setFieldValue}
                      value={props.values.receiver}
                    />
                  </Box>
                  {homeConfig && (
                    <Fees
                      amountFormikName="tokenAmount"
                      className=""
                      fee={bridgeFee}
                      networkFee={txFee}
                      feeSymbol={homeConfig?.nativeTokenSymbol}
                      symbol={
                        preflightDetails && tokens[preflightDetails.token]
                          ? tokens[preflightDetails.token].symbol
                          : undefined
                      }
                      error={props.errors.feeCheck}
                    />
                  )}
                  <Button type="submit" disabled={!homeConfig}>
                    Start transfer
                  </Button>
                  <Box mt={4}>
                    <HelpOutlineOutlinedIcon
                      onClick={() => setAboutOpen(true)}
                    />
                  </Box>
                </Box>
              );
            }}
          </Formik>
          {/* Form is end */}
        </Box>
      )}

      {aboutOpen && (
        <AboutDrawer open={aboutOpen} close={() => setAboutOpen(false)} />
      )}
      <ChangeNetworkDrawer
        open={changeNetworkOpen}
        close={() => setChangeNetworkOpen(false)}
      />

      {preflightModalOpen && (
        <PreflightModalTransfer
          open={preflightModalOpen}
          close={() => setPreflightModalOpen(false)}
          receiver={preflightDetails?.receiver || ""}
          sender={address || ""}
          start={() => {
            setPreflightModalOpen(false);
            preflightDetails &&
              deposit(
                preflightDetails.tokenAmount,
                preflightDetails.receiver,
                preflightDetails.token,
              );
          }}
          sourceNetwork={homeConfig?.name || ""}
          targetNetwork={destinationChainConfig?.name || ""}
          tokenSymbol={preflightDetails?.tokenSymbol || ""}
          value={preflightDetails?.tokenAmount || 0}
        />
      )}
      {!!transactionStatus && (
        <TransferActiveModal
          open={!!transactionStatus}
          close={resetDeposit}
          handleClick={handleClick}
        />
      )}
      {/* This is here due to requiring router */}
      <NetworkUnsupportedModal />
    </Box>
  );
}
